import Vue from 'vue';
import Router from 'vue-router';
import multiguard from 'vue-router-multiguard';
import guards from './guards';

const FrontPage = () => import('@/components/pages/FrontPage');
const Login = () => import('@/components/auth/Login');
const Invitation = () => import('@/components/auth/Invitation');
const AutoLogin = () => import('@/components/auth/AutoLogin');
const Register = () => import('@/components/auth/Register');
const ResetPasswordRequest = () => import('@/components/auth/ResetPasswordRequest');
const ResetPassword = () => import('@/components/auth/ResetPassword');
const ResetPasswordSent = () => import('@/components/auth/ResetPasswordSent');
const NoOrganisation = () => import('@/components/organisation/NoOrganisation');

const Merchant = () => import('@/components/business/merchant/Merchant');
const BusinessList = () => import('@/components/business/BusinessList');
const CreateMerchant = () => import('@/components/business/merchant/CreateMerchant');
const Chat = () => import('@/components/business/chat/Chat');

const OrganisationList = () => import('@/components/organisation/OrganisationList');
const Organisation = () => import('@/components/organisation/Organisation');
const OrganisationChat = () => import('@/components/organisation/OrganisationChat');

const Automation = () => import('@/components/business/automation/Automation');
const AutomatedResponses = () => import('@/components/business/automation/AutomatedResponses');

const Notifications = () => import('@/components/business/notifications/Notifications');
const SentNotifications = () => import('@/components/business/notifications/SentNotifications');
const CampaignDetails = () => import('@/components/business/notifications/CampaignDetails');

const TemplateMessages = () => import('@/components/business/notifications/TemplateMessages');
// const Subscribers = () => import('@/components/business/notifications/Subscribers');
const SubscriberProfile = () => import('@/components/business/contacts/Profile');
// const SignupFormBuilder = () => import('@/components/business/notifications/SignupFormBuilder');
// const CheckboxPlugin = () => import('@/components/business/notifications/CheckboxPlugin');

const Analytics = () => import('@/components/business/analytics/Analytics');
const Insights = () => import('@/components/business/analytics/Insights');

const Contacts = () => import('@/components/business/contacts/Contacts');
const ContactProfile = () => import('@/components/business/contacts/Profile');

const PageForbidden = () => import('@/components/business/PageForbidden');
const NoPlanSubscription = () => import('@/components/business/NoPlanSubscription');
const PlanSubscription = () => import('@/components/business/PlanSubscription');

const AccountNav = () => import('@/components/member/AccountNav');
const Profile = () => import('@/components/member/Profile');
const Email = () => import('@/components/member/Email');
const Password = () => import('@/components/member/Password');

const NumberManage = () => import('@/components/manage/NumberManage');
const SettingsNav = () => import('@/components/business/settings/SettingsNav');
const BusinessSettings = () => import('@/components/business/settings/BusinessSettings');
const AvailabilitySettings = () => import('@/components/business/settings/AvailabilitySettings');
const AutomationSettings = () => import('@/components/business/settings/AutomationSettings');
const SubscriberSettings = () => import('@/components/business/settings/SubscriberSettings');
const ContactSettings = () => import('@/components/business/settings/ContactSettings');
const OrganisationInfo = () => import('@/components/business/settings/OrganisationInfo');
const NumbersSettings = () => import('@/components/business/settings/NumbersSettings');
const TeamSettings = () => import('@/components/business/settings/TeamSettings');
const TeamMemberSettings = () => import('@/components/business/settings/TeamMemberSettings');
const PermissionSettings = () => import('@/components/business/settings/PermissionSettings');
const PlanBillingSettings = () => import('@/components/business/settings/PlanBillingSettings');
const BillingSettings = () => import('@/components/business/settings/BillingSettings');

const WhatsAppChannel = () => import('@/components/manage/WhatsAppChannel');
const SBFManage = () => import('@/components/manage/SBFManage');
const DevelopersManage = () => import('@/components/manage/DevelopersManage');

const ApiKeySettings = () => import('@/components/business/settings/api/ApiKeySettings');
const WebhookSettings = () => import('@/components/business/settings/api/WebhookSettings');
const WebhookLogs = () => import('@/components/business/settings/api/WebhookLogs');

const WhatsappConnectSettings = () => import('@/components/business/settings/whatsapp/WhatsappConnectSettings');
const WhatsappProfileSettings = () => import('@/components/business/settings/whatsapp/WhatsappProfileSettings');

const WhatsAppProfileSettings = () => import('@/components/business/settings/whatsapp/WhatsAppProfile.settings');

const IntegrationList = () => import('@/components/business/settings/integrations/IntegrationList');
const FrontIntegrationSettings = () => import('@/components/business/settings/integrations/FrontIntegrationSettings');

const Settings = () => import('@/components/settings/Settings');
const ProfileSettings = () => import('@/components/settings/ProfileSettings');
const EmailSettings = () => import('@/components/settings/EmailSettings');
const PhoneSettings = () => import('@/components/settings/PhoneSettings');
const PasswordSettings = () => import('@/components/settings/PasswordSettings');
const UserNotificationSettings = () => import('@/components/settings/UserNotificationSettings');
const NotificationsSettings = () => import('@/components/business/settings/NotificationSettings');

// const SignupForm = () => import('@/components/outer/signup_form/SignupForm');

const StripeCallback = () => import('@/components/pages/StripeCallback');
const NotFound = () => import('@/components/errors/NotFound');
const NotAllowed = () => import('@/components/errors/NotAllowed');
const ErrorPage = () => import('@/components/errors/ErrorPage');
const ComingSoon = () => import('@/components/common/ComingSoon');

const ContactFieldsSettings = () => import('@/components/business/settings/ContactFieldsSettings');
const WorkingHours = () => import('@/components/business/settings/WorkingHours');
const TagsSettings = () => import('@/components/business/settings/TagsSettings');
const QuickRepliesSettings = () => import('@/components/business/settings/QuickRepliesSettings');
const ConsentSettings = () => import('@/components/business/settings/ConsentSettings');
const MessageTemplates = () => import('@/components/business/settings/MessageTemplates');
const MessageTemplatesActivity = () => import('@/components/business/settings/MessageTemplatesActivity');
const BlocklistSettings = () => import('@/components/business/settings/BlocklistSettings');
const ApiSettings = () => import('@/components/business/settings/subSections/ApiSettings');
const WebhooksSettings = () => import('@/components/business/settings/subSections/WebhooksSettings');
const WebhookLogsSettings = () => import('@/components/business/settings/subSections/WebhookLogs');

const AutomatedResponsesSettings = () => import('@/components/business/settings/subSections/AutomatedResponsesSettings');
const AutomatedResponsesPreferenceSettings = () => import('@/components/business/settings/subSections/AutomatedResponsesPreferenceSettings');

const Report = () => import('@/components/business/analytics/Report');

Vue.use(Router);

export default new Router({
  mode: 'history',
  routes: [
    {
      path: '/no-organisation',
      name: 'NoOrganisation',
      component: NoOrganisation,
      beforeEnter: multiguard([
        guards.showSplash,
        guards.checkToken,
      ]),
    },
    {
      path: '/',
      name: 'FrontPage',
      component: FrontPage,
      redirect: {
        name: 'LoginPage',
      },
    },
    {
      path: '/invitation',
      name: 'Invitation',
      component: Invitation,
      beforeEnter: multiguard([
        guards.showSplash,
      ]),
      meta: {
        layout: 'AuthLayout',
        title: 'Invitation',
      },
    },
    {
      path: '/login',
      name: 'LoginPage',
      component: Login,
      beforeEnter: multiguard([
        guards.checkTokenOpposite,
      ]),
      props: true,
      meta: {
        layout: 'AuthLayout',
        title: 'Login',
      },
    },
    {
      path: '/autologin',
      name: 'AutoLogin',
      component: AutoLogin,
      beforeEnter: multiguard([
        guards.checkTokenOpposite,
      ]),
      meta: {
        layout: 'AuthLayout',
      },
    },
    {
      path: '/register',
      name: 'RegisterPage',
      component: Register,
      beforeEnter: multiguard([
        guards.checkTokenOpposite,
      ]),
      props: true,
      meta: {
        layout: 'AuthLayout',
        title: 'Register',
      },
    },
    {
      path: '/reset-password-request',
      name: 'ResetPasswordRequest',
      component: ResetPasswordRequest,
      beforeEnter: multiguard([
        guards.checkTokenOpposite,
      ]),
      meta: {
        layout: 'AuthLayout',
        title: 'Reset Password',
      },
    },
    {
      path: '/reset-password',
      name: 'ResetPassword',
      component: ResetPassword,
      beforeEnter: multiguard([
        guards.checkTokenOpposite,
      ]),
      meta: {
        layout: 'AuthLayout',
        title: 'Reset Password',
      },
    },
    {
      path: '/reset-password-sent',
      name: 'ResetPasswordSent',
      component: ResetPasswordSent,
      beforeEnter: multiguard([
        guards.checkTokenOpposite,
      ]),
      props: true,
      meta: {
        layout: 'AuthLayout',
        title: 'Reset Password Sent',
      },
    },
    {
      path: '/business-list',
      name: 'BusinessList',
      component: BusinessList,
      beforeEnter: multiguard([
        guards.checkToken,
      ]),
      meta: {
        layout: 'DefaultLayout',
        title: 'Business List',
      },
    },
    {
      path: '/logged-in-landing-page',
      name: 'logged-in-landing-page',
      // redirect: { name: 'Organisation' },
      redirect: { name: 'OrgContacts' },
    },
    {
      path: '/organisation-list',
      name: 'OrganisationList',
      component: OrganisationList,
      beforeEnter: multiguard([
        guards.checkToken,
      ]),
      meta: {
        layout: 'DefaultLayout',
        title: 'Organisation List',
      },
    },
    {
      path: '/organisation-create',
      name: 'CreateOrganisation',
      component: ComingSoon,
      beforeEnter: multiguard([
        guards.checkToken,
      ]),
      meta: {
        layout: 'DefaultLayout',
        title: 'Create Organisation',
      },
    },
    {
      path: '/member',
      component: Organisation,
      beforeEnter: multiguard([
        guards.checkToken,
        guards.showSplash,
      ]),
      children: [
        {
          path: 'account',
          component: AccountNav,
          meta: {},
          children: [
            {
              path: 'profile',
              name: 'ProfileInfo',
              component: Profile,
              meta: {
                layout: 'OrganisationLayout',
                title: 'Profile info',
                skipOrgCheck: true,
                ignoreActivation: true,
              },
            },
            {
              path: 'email',
              name: 'Email',
              component: Email,
              meta: {
                layout: 'OrganisationLayout',
                title: 'Profile email',
                skipOrgCheck: true,
                ignoreActivation: true,
              },
            },
            {
              path: 'password',
              name: 'Password',
              component: Password,
              meta: {
                layout: 'OrganisationLayout',
                title: 'Profile password',
                skipOrgCheck: true,
                ignoreActivation: true,
              },
            },
          ],
        },
      ],
    },
    {
      path: '/org',
      component: Organisation,
      beforeEnter: multiguard([
        guards.checkToken,
        guards.showSplash,
      ]),
      // props: true, // pass router props to component
      children: [
        // /org
        // Check if there is a previous visited org in the localstorage and redirect to it or get all organisations and redirect to first
        {
          path: '',
          name: 'Organisation',
          beforeEnter: guards.redirectToOrg,
        },

        // /org/123456789
        {
          path: ':organisationId/my',
          name: 'OrganisationWithId',
          beforeEnter: guards.checkOrgId,
          component: OrganisationChat,
          props: true,
          meta: {
            layout: 'OrganisationLayout',
          },
        },

        // /org/123456789/c/123456789
        {
          path: ':organisationId/my/n/:businessId/c/:conversationId',
          name: 'OrganisationConversation',
          component: OrganisationChat,
          props: true,
          meta: {
            layout: 'OrganisationLayout',
          },
        },

        // /org/123456789/n/123456789/c/123456789
        {
          path: ':organisationId/n/:businessId/c/:conversationId',
          name: 'OrganisationBusinessConversation',
          component: OrganisationChat,
          props: true,
          meta: {
            layout: 'OrganisationLayout',
          },
        },

        // /org/123456789/n/123456789
        {
          path: ':organisationId/n/:businessId/',
          name: 'OrganisationBusiness',
          component: OrganisationChat,
          props: true,
          meta: {
            layout: 'OrganisationLayout',
          },
        },
        {
          path: ':organisationId/n/:businessId/manage',
          component: NumberManage,
          children: [
            {
              path: '',
              name: 'NumberManage',
              redirect: {
                name: 'WAManage',
              },
            },
            {
              path: 'WhatsApp',
              name: 'WAManage',
              component: WhatsAppChannel,
              meta: {
                layout: 'OrganisationLayout',
                title: 'WhatsApp',
                manageScreen: true,
              },
            },
            {
              path: 'front',
              name: 'SBFManage',
              component: SBFManage,
              meta: {
                layout: 'OrganisationLayout',
                title: 'Front',
                manageScreen: true,
              },
            },
            {
              path: 'developers',
              name: 'DevelopersManage',
              component: DevelopersManage,
              meta: {
                layout: 'OrganisationLayout',
                title: 'Developers',
                manageScreen: true,
              },
            },
            {
              path: 'developers/api',
              name: 'ApiSettings',
              component: ApiSettings,
              meta: {
                layout: 'OrganisationLayout',
                title: 'API',
                manageScreen: true,
              },
            },
            {
              path: 'developers/webhooks',
              name: 'WebhooksSettings',
              component: WebhooksSettings,
              meta: {
                layout: 'OrganisationLayout',
                title: 'Webhooks',
                manageScreen: true,
              },
            },
            {
              path: 'developers/webhooklogs',
              name: 'WebhookLogsSettings',
              component: WebhookLogsSettings,
              meta: {
                layout: 'OrganisationLayout',
                title: 'Webhook logs',
                manageScreen: true,
              },
            },
            {
              path: 'WhatsApp/automated-responses',
              name: 'AutomatedResponsesSettings',
              component: AutomatedResponsesSettings,
              meta: {
                layout: 'OrganisationLayout',
                title: 'Automated responses',
                manageScreen: true,
              },
            },
            {
              path: 'automated-responses-preference',
              name: 'AutomatedResponsesPreferenceSettings',
              component: AutomatedResponsesPreferenceSettings,
              meta: {
                layout: 'OrganisationLayout',
                title: 'Automated responses',
                manageScreen: true,
              },
            },
            {
              path: 'WhatsApp/message-templates',
              name: 'MessageTemplates',
              component: MessageTemplates,
              meta: {
                layout: 'OrganisationLayout',
                title: 'Message Templates',
                manageScreen: true,
              },
            },
            {
              path: 'WhatsApp/whatsapp-profile',
              name: 'WhatsAppProfile',
              component: WhatsAppProfileSettings,
              meta: {
                layout: 'OrganisationLayout',
                title: 'Profile',
                manageScreen: true,
              },
            },
            {
              path: 'working-hours',
              name: 'WorkingHours',
              component: WorkingHours,
              meta: {
                layout: 'OrganisationLayout',
                title: 'Working hours',
                manageScreen: true,
              },
            },
            {
              path: 'consent',
              name: 'ConsentManagement',
              component: ConsentSettings,
              meta: {
                layout: 'OrganisationLayout',
                title: 'Consent management',
                manageScreen: true,
              },
            },
            {
              path: 'blocklist',
              name: 'BlocklistSettings',
              component: BlocklistSettings,
              meta: {
                layout: 'OrganisationLayout',
                title: 'Blocklist',
                manageScreen: true,
              },
            },
          ],
        },
        {
          path: ':organisationId?/contacts',
          name: 'OrgContacts',
          beforeEnter: guards.checkAndSetOrg,
          component: Contacts,
          meta: {
            layout: 'OrganisationLayout',
            title: 'Contacts',
          },
        },
        {
          path: ':organisationId/analytics',
          name: 'OrgAnalytics',
          component: Insights,
          meta: {
            layout: 'OrganisationLayout',
            title: 'Analytics',
          },
        },
        {
          path: ':organisationId/analytics/report/:section',
          name: 'Report',
          component: Report,
          meta: {
            layout: 'OrganisationLayout',
            title: 'Report',
          },
        },
        {
          path: ':organisationId/analytics/report/integration/:integration',
          name: 'ReportIntegration',
          component: Report,
          meta: {
            layout: 'OrganisationLayout',
            title: 'Report',
          },
        },
        {
          path: ':organisationId/settings',
          // NOTE: To route the app to settings page use the default child route name. There you can set the default settings page.
          // If we would use the name of this parent settings route we would need to set redirect parameter and that would give us console error "Failed to resolve async component" and a blank page.
          // name: 'do not use this route name - check NOTE above',
          component: SettingsNav,
          meta: {
            title: 'Settings',
          },
          children: [
            {
              path: '',
              name: 'OrgSettings',
              redirect: {
                name: 'MyNotifications',
              },
            },
            {
              path: 'notifications',
              name: 'MyNotifications',
              component: NotificationsSettings,
              meta: {
                layout: 'OrganisationLayout',
                title: 'Notifications',
              },
            },
            {
              path: 'organisation-info',
              name: 'OrganisationInfo',
              component: OrganisationInfo,
              meta: {
                layout: 'OrganisationLayout',
                title: 'Organisation Info',
              },
            },
            {
              path: 'numbers',
              name: 'NumbersSettings',
              component: NumbersSettings,
              meta: {
                layout: 'OrganisationLayout',
                title: 'Team Members Settings',
              },
            },
            {
              path: 'members',
              name: 'TeamMemberSettings',
              component: TeamMemberSettings,
              meta: {
                layout: 'OrganisationLayout',
                title: 'Team Members Settings',
              },
            },
            {
              path: 'billing',
              name: 'BillingSettings',
              component: BillingSettings,
              meta: {
                layout: 'OrganisationLayout',
                title: 'Billing',
              },
            },
            {
              path: 'permissions',
              name: 'PermissionSettings',
              component: PermissionSettings,
              meta: {
                layout: 'OrganisationLayout',
                title: 'Permissions',
              },
            },
            {
              path: 'contact-fields',
              name: 'ContactFieldsSettings',
              component: ContactFieldsSettings,
              meta: {
                layout: 'OrganisationLayout',
                title: 'Contact fields settings',
              },
            },
            {
              path: 'tags',
              name: 'TagsSettings',
              component: TagsSettings,
              meta: {
                layout: 'OrganisationLayout',
                title: 'Tags settings',
              },
            },
            {
              path: 'quick-replies',
              name: 'QuickRepliesSettings',
              component: QuickRepliesSettings,
              meta: {
                layout: 'OrganisationLayout',
                title: 'Quick replies settings',
              },
            },
          ],
        },
        {
          path: ':organisationId/number/:numberId/settings',
          // NOTE: To route the app to settings page use the default child route name. There you can set the default settings page.
          // If we would use the name of this parent settings route we would need to set redirect parameter and that would give us console error "Failed to resolve async component" and a blank page.
          // name: 'do not use this route name - check NOTE above',
          component: SettingsNav,
          meta: {
            title: 'Settings',
          },
          children: [
            {
              path: '',
              name: 'NumSettings',
              redirect: {
                name: 'BusinessProfile',
              },
            },
            {
              path: 'message-templates-activity',
              name: 'MessageTemplatesActivity',
              component: MessageTemplatesActivity,
              meta: {
                layout: 'OrganisationLayout',
                title: 'Message Templates Activity',
              },
            },
          ],
        },
        {
          path: 'todo',
          name: 'OrgTodo',
          component: ComingSoon,
          meta: {
            layout: 'OrganisationLayout',
            title: 'TO-DO',
          },
        },
      ],
    },
    {
      path: '/merchant/:merchant_id',
      name: 'Merchant',
      component: Merchant,
      beforeEnter: multiguard([
        guards.checkToken,
        guards.isMerchantMember,
        guards.isMerchantSubscribed,
      ]),
      redirect: {
        name: 'Chat',
      },
      children: [
        {
          path: 'messages',
          name: 'Chat',
          component: Chat,
          meta: {
            layout: 'BusinessLayout',
            title: 'Chat',
          },
        },
        {
          path: 'notifications',
          name: 'Notifications',
          component: Notifications,
          meta: {
            layout: 'BusinessLayout',
            title: 'Notifications',
          },
          redirect: {
            name: 'SentNotifications',
          },
          children: [
            {
              path: 'sent',
              name: 'SentNotifications',
              component: SentNotifications,
              meta: {
                layout: 'BusinessLayout',
                title: 'Sent Notifications',
              },
            },
            {
              path: 'template-messages',
              name: 'TemplateMessages',
              component: TemplateMessages,
              meta: {
                layout: 'BusinessLayout',
                title: 'Template Messages',
              },
            },
          ],
        },
        {
          path: 'notifications/campaign/:campaign_id',
          name: 'CampaignDetails',
          component: CampaignDetails,
          meta: {
            layout: 'BusinessLayout',
            title: 'Campaign Details',
          },
        },
        // {
        //   path: 'notifications/lists/:list_id',
        //   name: 'SubscriberList',
        //   component: SubscriberList,
        //   meta: {
        //     layout: 'BusinessLayout',
        //     title: 'Subscriber List',
        //   },
        //   redirect: {
        //     name: 'Subscribers',
        //   },
        //   children: [
        //     {
        //       path: 'subscribers',
        //       name: 'Subscribers',
        //       component: Subscribers,
        //       meta: {
        //         layout: 'BusinessLayout',
        //         title: 'Subscribers',
        //       },
        //     },
        //     {
        //       path: 'form',
        //       name: 'SignupFormBuilder',
        //       component: SignupFormBuilder,
        //       meta: {
        //         layout: 'BusinessLayout',
        //         title: 'Signup Form',
        //       },
        //     },
        //     {
        //       path: 'checkbox',
        //       name: 'CheckboxPlugin',
        //       component: CheckboxPlugin,
        //       meta: {
        //         layout: 'BusinessLayout',
        //         title: 'Checkbox Plugin',
        //       },
        //     },
        //   ],
        // },
        {
          path: 'automation',
          name: 'Automation',
          component: Automation,
          beforeEnter: multiguard([
            guards.hasManageAutomationPermission,
          ]),
          meta: {
            layout: 'BusinessLayout',
            title: 'Automation',
          },
          redirect: {
            name: 'AutomatedResponses',
          },
          children: [
            {
              path: 'responses',
              name: 'AutomatedResponses',
              component: AutomatedResponses,
              meta: {
                layout: 'BusinessLayout',
                title: 'Automated Responses',
              },
            },
          ],
        },
        {
          path: 'analytics',
          name: 'Analytics',
          component: Analytics,
          beforeEnter: multiguard([
            guards.hasViewAnalyticsPermission,
          ]),
          meta: {
            layout: 'BusinessLayout',
            title: 'Analytics',
          },
        },
        {
          path: 'contacts',
          name: 'Contacts',
          component: Contacts,
          meta: {
            layout: 'BusinessLayout',
            title: 'Contacts',
          },
        },
        {
          path: 'contacts/:contact_id',
          name: 'ContactProfile',
          component: ContactProfile,
          meta: {
            layout: 'BusinessLayout',
            title: 'Contact Profile',
          },
        },
        {
          path: 'lists/:list_id/subscribers/:contact_id',
          name: 'SubscriberProfile',
          component: SubscriberProfile,
          meta: {
            layout: 'BusinessLayout',
            title: 'Subscriber Profile',
          },
        },
        {
          path: 'forbidden',
          name: 'PageForbidden',
          component: PageForbidden,
          meta: {
            layout: 'BusinessLayout',
            title: 'Page Forbidden',
          },
        },
        // {
        //   path: 'payments',
        //   name: 'Payments',
        //   component: Payments,
        //   meta: {
        //     layout: 'BusinessLayout',
        //     title: 'Payments',
        //   },
        // },
        // {
        //   path: 'payments/new',
        //   name: 'NewPayment',
        //   component: NewPayment,
        //   meta: {
        //     layout: 'BusinessLayout',
        //     title: 'New Payment',
        //   },
        // },
        // {
        //   path: 'invoices/:invoice_id',
        //   name: 'InvoicePage',
        //   component: Invoice,
        //   meta: {
        //     layout: 'BusinessLayout',
        //     title: 'Invoice',
        //   },
        // },
        {
          path: 'settings',
          name: 'BusinessSettings',
          component: BusinessSettings,
          beforeEnter: multiguard([
            guards.hasManageSettingsPermission,
          ]),
          redirect: {
            name: 'AvailabilitySettings',
          },
          children: [
            {
              path: 'availability',
              name: 'AvailabilitySettings',
              component: AvailabilitySettings,
              meta: {
                layout: 'BusinessSettingsLayout',
                title: 'Availability Settings',
              },
            },
            {
              path: 'automation',
              name: 'AutomationSettings',
              component: AutomationSettings,
              meta: {
                layout: 'BusinessSettingsLayout',
                title: 'Automation Settings',
              },
            },
            {
              path: 'subscribers',
              name: 'SubscriberSettings',
              component: SubscriberSettings,
              meta: {
                layout: 'BusinessSettingsLayout',
                title: 'Subscriber Settings',
              },
            },
            {
              path: 'contacts',
              name: 'ContactSettings',
              component: ContactSettings,
              meta: {
                layout: 'BusinessSettingsLayout',
                title: 'Contact Settings',
              },
            },
            // {
            //   path: 'taxes',
            //   name: 'TaxesSettings',
            //   component: TaxesSettings,
            //   meta: {
            //     layout: 'BusinessSettingsLayout',
            //     title: 'Taxes Settings',
            //   },
            // },
            // {
            //   path: 'payments',
            //   name: 'PaymentSettings',
            //   component: PaymentSettings,
            //   meta: {
            //     layout: 'BusinessSettingsLayout',
            //     title: 'Payment Settings',
            //   },
            // },
            // {
            //   path: 'providers',
            //   name: 'PaymentGatewaySettings',
            //   component: PaymentGatewaySettings,
            //   beforeEnter: multiguard([
            //     guards.userIsMerchantAdmin,
            //   ]),
            //   meta: {
            //     layout: 'BusinessSettingsLayout',
            //     title: 'Payment Gateway Settings',
            //   },
            // },
            // {
            //   path: 'business-info',
            //   name: 'BusinessInfoSettings',
            //   component: BusinessInfoSettings,
            //   meta: {
            //     layout: 'BusinessSettingsLayout',
            //     title: 'Business Profile Settings',
            //   },
            // },
            {
              path: 'teams',
              name: 'TeamSettings',
              component: TeamSettings,
              meta: {
                layout: 'BusinessSettingsLayout',
                title: 'Team Settings',
              },
            },
            {
              path: 'members',
              name: 'TeamMemberSettings_old',
              component: TeamMemberSettings,
              meta: {
                layout: 'BusinessSettingsLayout',
                title: 'Team Members Settings',
              },
            },
            {
              path: 'permissions',
              name: 'PermissionSettings_old',
              component: PermissionSettings,
              meta: {
                layout: 'BusinessSettingsLayout',
                title: 'Permissions',
              },
            },
            {
              path: 'plan-billing',
              name: 'PlanBillingSettings',
              component: PlanBillingSettings,
              beforeEnter: multiguard([
                guards.isMerchantOwner,
              ]),
              meta: {
                layout: 'BusinessSettingsLayout',
                title: 'Plan Settings',
              },
            },
            {
              path: 'api-keys',
              name: 'ApiKeySettings',
              component: ApiKeySettings,
              meta: {
                layout: 'BusinessSettingsLayout',
                title: 'Api Keys',
              },
            },
            {
              path: 'webhooks',
              name: 'WebhookSettings',
              component: WebhookSettings,
              meta: {
                layout: 'BusinessSettingsLayout',
                title: 'Webhook Settings',
              },
            },
            {
              path: 'webhooks-logs',
              name: 'WebhookLogs',
              component: WebhookLogs,
              meta: {
                layout: 'BusinessSettingsLayout',
                title: 'Webhook Logs',
              },
            },

            {
              path: 'whatsapp/connect',
              name: 'WhatsappConnectSettings',
              component: WhatsappConnectSettings,
              meta: {
                layout: 'BusinessSettingsLayout',
                title: 'Connect WhatsApp Account',
              },
            },
            {
              path: 'whatsapp/profile',
              name: 'WhatsappProfileSettings',
              component: WhatsappProfileSettings,
              meta: {
                layout: 'BusinessSettingsLayout',
                title: 'Edit WhatsApp Profile',
              },
            },
            {
              path: 'integrations',
              name: 'IntegrationList',
              component: IntegrationList,
              meta: {
                layout: 'BusinessSettingsLayout',
                title: 'Integrations',
              },
            },
            {
              path: 'integrations/front',
              name: 'FrontIntegrationSettings',
              component: FrontIntegrationSettings,
              meta: {
                layout: 'BusinessSettingsLayout',
                title: 'Front Integration',
              },
            },
          ],
        },
      ],
    },
    {
      path: '/front-integration',
      beforeEnter: (to, from, next) => {
        const { query } = to;
        if (query.error) {
          next({
            name: 'IntegrationList',
            params: { merchant_id: query.state },
          });
        } else {
          next({
            name: 'FrontIntegrationSettings',
            params: { merchant_id: query.state },
            query: { step: 'token', code: query.code },
          });
        }
      },
    },
    {
      path: '/merchant/:merchant_id/no-plan',
      name: 'NoPlanSubscription',
      component: NoPlanSubscription,
      beforeEnter: multiguard([
        guards.needsToSubscribe,
      ]),
      meta: {
        layout: 'DefaultLayout',
        title: 'Subscription',
      },
    },
    {
      path: '/merchant/:merchant_id/subscribe',
      name: 'PlanSubscription',
      component: PlanSubscription,
      beforeEnter: multiguard([
        guards.needsToSubscribe,
      ]),
      meta: {
        layout: 'DefaultLayout',
        title: 'Subscription',
      },
    },
    {
      path: '/create-merchant',
      name: 'CreateMerchant',
      component: CreateMerchant,
      meta: {
        layout: 'DefaultLayout',
        title: 'Create Merchant',
      },
      beforeEnter: multiguard([
        guards.checkToken,
      ]),
    },
    {
      path: '/settings',
      name: 'Settings',
      component: Settings,
      redirect: {
        name: 'ProfileSettings',
      },
      children: [
        {
          path: 'profile',
          name: 'ProfileSettings',
          component: ProfileSettings,
          meta: {
            layout: 'SettingsLayout',
            title: 'Profile',
          },
        },
        {
          path: 'email',
          name: 'EmailSettings',
          component: EmailSettings,
          meta: {
            layout: 'SettingsLayout',
            title: 'Change email',
          },
        },
        {
          path: 'phone',
          name: 'PhoneSettings',
          component: PhoneSettings,
          meta: {
            layout: 'SettingsLayout',
            title: 'Change Phone',
          },
        },
        {
          path: 'password',
          name: 'PasswordSettings',
          component: PasswordSettings,
          meta: {
            layout: 'SettingsLayout',
            title: 'Change Password',
          },
        },
        {
          path: 'notification',
          name: 'UserNotificationSettings',
          component: UserNotificationSettings,
          meta: {
            layout: 'SettingsLayout',
            title: 'Notification Settings',
          },
        },
      ],
    },
    // {
    //   path: '/forms/:form_id',
    //   name: 'SignupForm',
    //   component: SignupForm,
    //   meta: {
    //     layout: 'OuterLayout',
    //     title: 'Signup Form',
    //   },
    // },
    // {
    //   path: '/i/:invoice_id',
    //   name: 'Checkout',
    //   component: Checkout,
    //   meta: {
    //     layout: 'CheckoutLayout',
    //     title: 'Checkout',
    //   },
    // },
    {
      path: '/stripe-callback',
      name: 'StripeCallback',
      component: StripeCallback,
      meta: {
        layout: 'AuthLayout',
      },
    },
    {
      path: '/405',
      name: 'NotAllowed',
      component: NotAllowed,
      meta: {
        layout: 'ErrorLayout',
        title: 'Page Not Allowed',
      },
    },
    {
      path: '/error',
      name: 'ErrorPage',
      component: ErrorPage,
      props: true,
      meta: {
        layout: 'ErrorLayout',
        title: 'Error',
      },
    },
    {
      path: '/404',
      name: 'NotFound',
      component: NotFound,
      meta: {
        layout: 'ErrorLayout',
        title: 'Page Not Found',
      },
    },
    {
      path: '*',
      redirect: {
        name: 'NotFound',
      },
    },
  ],
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
});
